import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import EditPackageDialog from '../../EditPackageDialog';
import { showToastError, showToastSuccess } from '../../components/ToastNotifications';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import {
  editPackageStaged,
  getPackageStagedById,
  getPackagesStagedTotals,
} from '../../hooks/usePackages';

export default function EditStagedPackageContainer() {
  const location = useLocation();
  const navigate = useNavigate();
  const { org } = useLegacyAppSession();
  const [pkg, setPkg] = useState(location.state?.pkg || null);
  const [isLoading, setIsLoading] = useState(!location.state?.pkg);
  const [dialogVersion, setDialogVersion] = useState(0);

  useEffect(() => {
    if (pkg || !location.state?.pkgLabel || !org?.id) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getPackageStagedById(org.id, location.state.pkgLabel)
      .then((result) => {
        if (cancelled) return;
        setPkg(result || null);
      })
      .catch(() => {
        if (cancelled) return;
        showToastError('Staged package could not be loaded.');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [location.state, org?.id, pkg]);

  if (isLoading) {
    return null;
  }

  if (!pkg) {
    return <Navigate to="/packages" replace />;
  }

  return (
    <EditPackageDialog
      key={dialogVersion}
      organizationId={org?.id ? String(org.id) : undefined}
      pkg={{
        ...pkg,
        id: pkg.packageLabel,
        label: pkg.packageLabel,
        productId: pkg.productId,
        facilityId: pkg.facilityId,
        productName: pkg.productName,
      }}
      isStaged
      stagedCoaData={pkg.data}
      onClose={() => navigate('/packages', { replace: true })}
      onSave={async (payload, exit: boolean = true) => {
        if (!org?.id) return;
        try {
          const uniqueFileIds = [...new Set(payload?.coaFileIds || [])];
          await editPackageStaged({
            organizationId: org.id,
            packageLabel: pkg.packageLabel,
            packageCoa: payload.packageCoa,
            coaFileIds: uniqueFileIds,
            isManualEdit: true,
          });

          try {
            const refreshed = await getPackagesStagedTotals({
              organizationId: org.id,
              label: pkg.packageLabel,
              page: 0,
              pageSize: 50,
            });
            const freshRow = (refreshed?.data || []).find((row) => {
              const sameLabel = String(row?.packageLabel || '').toLowerCase()
                === String(pkg?.packageLabel || '').toLowerCase();
              const sameProduct = pkg?.productId == null || row?.productId == null
                || Number(row.productId) === Number(pkg.productId);
              const sameSource = pkg?.sourcePackageId == null || row?.sourcePackageId == null
                || Number(row.sourcePackageId) === Number(pkg.sourcePackageId);
              const sameFacility = pkg?.facilityId == null || row?.facilityId == null
                || Number(row.facilityId) === Number(pkg.facilityId);
              return sameLabel && sameProduct && sameSource && sameFacility;
            });

            if (freshRow) {
              setPkg((currentPackage) => ({
                ...(currentPackage || {}),
                ...freshRow,
                lastModified: freshRow.lastModified
                  ? new Date(freshRow.lastModified)
                  : freshRow.lastModified,
              }));
              setDialogVersion((version) => version + 1);
            }
          } catch (refreshError) {
            console.warn('[EditStagedPackageContainer] post-save refresh failed', refreshError);
          }

          if (exit) {
            showToastSuccess('Package successfully saved.');
            navigate('/packages', { replace: true });
          }
        } catch (error) {
          showToastError('Staged package could not be saved.');
          console.error(error);
        }
      }}
    />
  );
}
